import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, Smartphone, Copy, Check, Key, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 3;

// Animation variants
const fadeIn = { opacity: 1, y: 0 };
const fadeOut = { opacity: 0, y: 20 };

const AdminLoginPage = () => {
  const navigate = useNavigate();
  
  // Step: 'login' | 'mfa-enroll' | 'mfa-verify' | 'backup-codes'
  const [step, setStep] = useState('login');
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // MFA state
  const [totpCode, setTotpCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);
  
  // Recovery mode
  const [showRecovery, setShowRecovery] = useState(false);
  const [backupCodeInput, setBackupCodeInput] = useState('');
  
  // Session token for MFA flow
  const [sessionToken, setSessionToken] = useState('');
  
  const inputRefs = useRef([]);

  // Check if already logged in as admin with verified MFA
  useEffect(() => {
    const adminToken = sessionStorage.getItem('admin_session_token');
    if (adminToken) {
      axios.get(`${API_URL}/api/admin-auth/session`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      }).then(() => {
        navigate('/admin');
      }).catch(() => {
        sessionStorage.removeItem('admin_session_token');
      });
    }
  }, [navigate]);

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin-auth/login`, {
        email: email.trim().toLowerCase(),
        password
      });

      const { admin_session_token, requires_mfa_enrollment, requires_mfa_verification } = response.data;
      
      // Store the session token for MFA flow
      setSessionToken(admin_session_token);
      sessionStorage.setItem('admin_session_token', admin_session_token);

      if (requires_mfa_enrollment) {
        // First-time admin - needs to enroll in MFA
        await initMFAEnrollment(admin_session_token);
      } else if (requires_mfa_verification) {
        // Existing admin - needs to verify MFA
        setStep('mfa-verify');
        setAttempts(0);
      }

    } catch (err) {
      const message = err.response?.data?.detail || 'Authentication failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Initialize MFA enrollment
  const initMFAEnrollment = async (token) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin-auth/mfa/enroll`, {
        admin_session_token: token
      });

      setQrCode(response.data.qr_code);
      setManualKey(response.data.manual_entry_key);
      setStep('mfa-enroll');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to initialize MFA enrollment';
      if (message.includes('already enrolled')) {
        setStep('mfa-verify');
        return;
      }
      setError(message);
      toast.error(message);
    }
  };

  // Handle MFA enrollment verification
  const handleEnrollVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = sessionToken || sessionStorage.getItem('admin_session_token');
    if (!token) {
      resetToLogin('Session expired. Please login again.');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin-auth/mfa/complete-enrollment`, {
        admin_session_token: token,
        totp_code: totpCode
      });

      // Store the fully verified session token
      sessionStorage.setItem('admin_session_token', response.data.admin_session_token);
      setSessionToken(response.data.admin_session_token);
      
      // Show backup codes
      setBackupCodes(response.data.backup_codes);
      setStep('backup-codes');
      toast.success('MFA enrolled successfully!');

    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid verification code';
      setError(message);
      toast.error(message);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification (for existing admins)
  const handleMFAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = sessionToken || sessionStorage.getItem('admin_session_token');
    if (!token) {
      resetToLogin('Session expired. Please login again.');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin-auth/mfa/verify`, {
        admin_session_token: token,
        totp_code: totpCode
      });

      // Store the fully verified session token
      sessionStorage.setItem('admin_session_token', response.data.admin_session_token);
      toast.success('MFA verified successfully!');
      
      // Navigate DIRECTLY to admin dashboard
      navigate('/admin');

    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      const message = err.response?.data?.detail || 'Invalid verification code';
      
      if (newAttempts >= MAX_ATTEMPTS) {
        resetToLogin('Too many failed attempts. Please login again.');
        return;
      }
      
      setError(`${message} (${MAX_ATTEMPTS - newAttempts} attempt${MAX_ATTEMPTS - newAttempts !== 1 ? 's' : ''} remaining)`);
      toast.error(message);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // Handle backup code recovery
  const handleBackupCodeVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = sessionToken || sessionStorage.getItem('admin_session_token');
    if (!token) {
      resetToLogin('Session expired. Please login again.');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin-auth/mfa/use-backup-code`, {
        admin_session_token: token,
        backup_code: backupCodeInput.toUpperCase()
      });

      // Store the fully verified session token
      sessionStorage.setItem('admin_session_token', response.data.admin_session_token);
      
      toast.success(response.data.message);
      
      if (response.data.remaining_backup_codes < 3) {
        toast.warning(`Warning: Only ${response.data.remaining_backup_codes} backup codes remaining`);
      }
      
      // Navigate DIRECTLY to admin dashboard
      navigate('/admin');

    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid backup code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Reset to login step
  const resetToLogin = (message) => {
    sessionStorage.removeItem('admin_session_token');
    setSessionToken('');
    setStep('login');
    setTotpCode('');
    setAttempts(0);
    setShowRecovery(false);
    setBackupCodeInput('');
    setError('');
    if (message) {
      toast.error(message);
    }
  };

  // Copy functions
  const handleCopyKey = () => {
    navigator.clipboard.writeText(manualKey);
    setCopied(true);
    toast.success('Key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success('Backup codes copied to clipboard');
  };

  // Continue to dashboard after viewing backup codes
  const handleContinueToDashboard = () => {
    navigate('/admin');
  };

  // Handle TOTP code input with auto-submit
  const handleTotpChange = (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(cleanValue);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoMnY0aC0yem0tNiA2aC00djJoNHYtMnptMC02aC00djJoNHYtMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />

      <motion.div
        initial={fadeOut}
        animate={fadeIn}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative"
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 mb-4"
          >
            <Shield className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-slate-400 text-sm">
            {step === 'login' && 'Secure access for authorized personnel only'}
            {step === 'mfa-enroll' && 'Set up two-factor authentication'}
            {step === 'mfa-verify' && `Enter the code from your authenticator app`}
            {step === 'backup-codes' && 'Save your backup codes'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {/* LOGIN STEP */}
          {step === 'login' && (
            <motion.div
              key="login"
              initial={fadeOut}
              animate={fadeIn}
              exit={fadeOut}
              transition={{ duration: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl"
            >
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Admin Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-600 rounded-lg py-3 px-10 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="admin@insulnutrients.com"
                      required
                      autoComplete="email"
                      data-testid="admin-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-600 rounded-lg py-3 px-10 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all"
                  data-testid="admin-login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    'Sign in to Admin Portal'
                  )}
                </Button>
              </form>

              <p className="text-center text-slate-500 text-xs mt-6">
                This is a secure admin portal. All access attempts are logged.
                <br />Multi-factor authentication (MFA) is required.
              </p>

              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to Insul Nutrients
                </button>
              </div>
            </motion.div>
          )}

          {/* MFA ENROLLMENT STEP */}
          {step === 'mfa-enroll' && (
            <motion.div
              key="mfa-enroll"
              initial={fadeOut}
              animate={fadeIn}
              exit={fadeOut}
              transition={{ duration: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl"
            >
              <div className="text-center mb-6">
                <Smartphone className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-white">Set Up Authenticator</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Scan the QR code with Google Authenticator, Authy, or similar app
                </p>
              </div>

              {qrCode && (
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-3 rounded-xl">
                    <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                  </div>
                </div>
              )}

              <div className="mb-6">
                <p className="text-slate-400 text-xs text-center mb-2">Or enter this key manually:</p>
                <div className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-3">
                  <code className="flex-1 text-amber-400 text-sm font-mono break-all">
                    {manualKey}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>

              <form onSubmit={handleEnrollVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Enter 6-digit code from app
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => handleTotpChange(e.target.value)}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg py-3 px-4 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="mfa-enroll-code-input"
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all"
                  data-testid="mfa-enroll-verify-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Complete Setup'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => resetToLogin()}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </motion.div>
          )}

          {/* MFA VERIFY STEP */}
          {step === 'mfa-verify' && !showRecovery && (
            <motion.div
              key="mfa-verify"
              initial={fadeOut}
              animate={fadeIn}
              exit={fadeOut}
              transition={{ duration: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl"
            >
              <div className="text-center mb-6">
                <KeyRound className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-white">Two-Factor Authentication</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Enter the code from your authenticator app for {email}
                </p>
              </div>

              <form onSubmit={handleMFAVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    6-digit code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => handleTotpChange(e.target.value)}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg py-3 px-4 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="mfa-verify-code-input"
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all"
                  data-testid="mfa-verify-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </form>

              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => resetToLogin()}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to login
                </button>
                <button
                  onClick={() => setShowRecovery(true)}
                  className="text-amber-500 hover:text-amber-400 text-sm transition-colors flex items-center gap-1"
                >
                  <Key className="w-4 h-4" />
                  Lost access? Use a backup code
                </button>
              </div>
            </motion.div>
          )}

          {/* BACKUP CODE RECOVERY */}
          {step === 'mfa-verify' && showRecovery && (
            <motion.div
              key="recovery"
              initial={fadeOut}
              animate={fadeIn}
              exit={fadeOut}
              transition={{ duration: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl"
            >
              <div className="text-center mb-6">
                <Key className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-white">Use Backup Code</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Enter one of your backup codes to sign in
                </p>
              </div>

              <form onSubmit={handleBackupCodeVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Backup Code (format: XXXX-XXXX)
                  </label>
                  <input
                    type="text"
                    value={backupCodeInput}
                    onChange={(e) => setBackupCodeInput(e.target.value.toUpperCase())}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg py-3 px-4 text-white text-center text-lg tracking-widest font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="XXXX-XXXX"
                    required
                    autoFocus
                    data-testid="backup-code-input"
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !backupCodeInput}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all"
                  data-testid="backup-code-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Use Backup Code'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setShowRecovery(false);
                    setBackupCodeInput('');
                    setError('');
                  }}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to authenticator code
                </button>
              </div>
            </motion.div>
          )}

          {/* BACKUP CODES DISPLAY (after enrollment) */}
          {step === 'backup-codes' && (
            <motion.div
              key="backup-codes"
              initial={fadeOut}
              animate={fadeIn}
              exit={fadeOut}
              transition={{ duration: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-500" />
                </div>
                <h2 className="text-lg font-semibold text-white">MFA Setup Complete!</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Save these backup codes securely - they won't be shown again
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="bg-slate-800 rounded px-3 py-2 text-amber-400 font-mono text-sm text-center">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleCopyBackupCodes}
                variant="outline"
                className="w-full mb-4 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All Codes
              </Button>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6">
                <p className="text-amber-400 text-sm text-center">
                  Each backup code can only be used once. Store them in a safe place.
                </p>
              </div>

              <Button
                onClick={handleContinueToDashboard}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all"
                data-testid="continue-to-dashboard-btn"
              >
                Continue to Dashboard
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
